// Sources — the "소재(소스) 관리" hub (route /sources).
//
// One place to decide what material every video is made from. Four tabs:
//   ① 트렌드 라인   — per factory line, pick 화제성(buzz) vs 카테고리(tag) selection.
//   ② 토픽 브라우저 — browse LIVE clustered trend topics and produce a story from one.
//   ③ 수동 입력     — paste a 소재/줄거리 and generate a story directly.
//   ④ 참조 영상     — adapt a YouTube video (or playlist/channel) into stories
//                     from its subtitles (POST /api/reference-video).
//
// The category picker + topic browser are driven by trend-collector's live
// catalog (GET /api/trend-categories, /api/trend-topics) — no hardcoded list.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ExternalLink, Flame, Loader2, Rss, Sparkles, Tags, Video } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type {
  AdaptationStrength,
  FactoryConfig,
  FactoryConfigInput,
  ReferenceAdaptInput,
  ReferenceResult,
  SelectMode,
  Story,
  StoryFormat,
  TrendCategory,
  TrendTopic,
  TrendTopicsQuery,
} from '@/api/types'
import { useCreateStory } from '@/hooks/useStories'
import {
  Badge,
  Button,
  Card,
  Chip,
  ConfirmDialog,
  EmptyState,
  FormRow,
  Input,
  Select,
  Tabs,
  Textarea,
} from '@/admin-design/_common'
import type { SelectOption } from '@/admin-design/_common'

type SourceTab = 'lines' | 'topics' | 'manual' | 'reference'

const FORMAT_OPTIONS: SelectOption[] = [
  { label: 'Short (1–3컷)', value: 'short' },
  { label: 'Narrative (막/씬)', value: 'narrative' },
]

const ADAPT_OPTIONS: SelectOption[] = [
  { label: '원작 충실', value: 'faithful' },
  { label: '균형', value: 'balanced' },
  { label: '재창작', value: 'reimagine' },
]

export default function Sources() {
  const [tab, setTab] = useState<SourceTab>('lines')

  return (
    <div className="min-w-0 space-y-4">
      <header>
        <div className="flex items-center gap-2">
          <Rss size={18} className="text-primary" />
          <h1 className="text-xl font-semibold text-foreground">소재 관리</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          영상 제작에 쓸 소재를 한곳에서 관리합니다 — 트렌드 라인 배정, 토픽 직접 선택, 소재 수동
          입력.
        </p>
      </header>

      <Tabs
        aria-label="소재 관리 탭"
        value={tab}
        onChange={(k) => setTab(k as SourceTab)}
        onInvalidValue={(k) => setTab(k as SourceTab)}
        items={[
          { key: 'lines', label: '① 트렌드 라인' },
          { key: 'topics', label: '② 토픽 브라우저' },
          { key: 'manual', label: '③ 수동 입력' },
          { key: 'reference', label: '④ 참조 영상' },
        ]}
      />

      {tab === 'lines' && <TrendLinesTab />}
      {tab === 'topics' && <TopicBrowserTab />}
      {tab === 'manual' && <ManualInputTab />}
      {tab === 'reference' && <ReferenceVideoTab />}
    </div>
  )
}

// ─── ① 트렌드 라인 ──────────────────────────────────────────────────────────

function TrendLinesTab() {
  const configsQ = useQuery({ queryKey: ['factory-configs'], queryFn: api.listFactoryConfigs })
  const catsQ = useQuery({ queryKey: ['trend-categories'], queryFn: api.getTrendCategories })

  const configs = configsQ.data ?? []
  const categories = catsQ.data ?? []
  const catError = catsQ.isError ? errorMessage(catsQ.error) : null

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        각 팩토리 라인이 어떤 소재를 각색할지 정합니다.{' '}
        <strong className="font-medium text-foreground">화제성</strong>은 클러스터된 인기 이슈를,{' '}
        <strong className="font-medium text-foreground">카테고리</strong>는 지정한 태그의 토픽만
        가져옵니다.
      </p>

      {configsQ.isLoading ? (
        <Card>
          <Card.Body>
            <p className="text-sm text-muted-foreground">라인 불러오는 중…</p>
          </Card.Body>
        </Card>
      ) : configs.length === 0 ? (
        <Card>
          <EmptyState
            title="팩토리 라인이 없어요."
            description="Factory에서 라인을 먼저 만드세요."
            action={
              <Button as={Link} to="/factory" variant="secondary" size="sm">
                Factory로 이동
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {configs.map((c) => (
            <LineSourceCard
              key={c.id}
              config={c}
              categories={categories}
              categoriesLoading={catsQ.isLoading}
              categoriesError={catError}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function LineSourceCard({
  config,
  categories,
  categoriesLoading,
  categoriesError,
}: {
  config: FactoryConfig
  categories: TrendCategory[]
  categoriesLoading: boolean
  categoriesError: string | null
}) {
  const qc = useQueryClient()
  const [mode, setMode] = useState<SelectMode>(config.select_mode)
  const [minScore, setMinScore] = useState(config.min_score)
  const [spikeOnly, setSpikeOnly] = useState(config.spike_only)
  const [tags, setTags] = useState<string[]>(parseTags(config.tags))

  const save = useMutation({
    mutationFn: () =>
      api.updateFactoryConfig(
        config.id,
        configToInput(config, {
          select_mode: mode,
          min_score: minScore >= 0 ? minScore : 0,
          spike_only: spikeOnly,
          tags: tags.join(','),
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['factory-configs'] }),
  })

  const toggleTag = (tag: string) =>
    setTags((cur) => (cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag]))

  const sortedCsv = (list: string[]) => [...list].sort().join(',')
  const dirty =
    mode !== config.select_mode ||
    minScore !== config.min_score ||
    spikeOnly !== config.spike_only ||
    sortedCsv(tags) !== sortedCsv(parseTags(config.tags))

  // Active tags not present in the live catalog (custom CSV tags set on the
  // line's Factory settings) — still render them so they can be removed.
  const extraTags = tags.filter((t) => !categories.some((c) => c.tag === t))

  return (
    <Card>
      <Card.Body className="min-w-0 space-y-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-card-foreground">
            {config.name}
          </span>
          <ModeToggle value={mode} onChange={setMode} />
        </div>

        {mode === 'buzz' ? (
          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
            <p className="text-xs leading-relaxed text-muted-foreground">
              클러스터된 화제성 높은 이슈를 spike/score 기준으로 뽑습니다. (아래 카테고리로 범위를
              선택적으로 좁힐 수 있어요.)
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-xs text-foreground">
                <span className="text-muted-foreground">최소 점수</span>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={minScore}
                  onChange={(e) => setMinScore(Number(e.target.value))}
                  disabled={save.isPending}
                  block={false}
                  className="w-24"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-foreground">
                <input
                  type="checkbox"
                  checked={spikeOnly}
                  onChange={(e) => setSpikeOnly(e.target.checked)}
                  disabled={save.isPending}
                  className="h-4 w-4 rounded border-border"
                />
                급상승(spike)만
              </label>
            </div>
          </div>
        ) : (
          <p className="text-xs leading-relaxed text-muted-foreground">
            선택한 카테고리(태그)의 토픽만 이 라인으로 들어옵니다. 여러 개는 OR로 합쳐집니다.
          </p>
        )}

        {/* Category picker — filter (tag mode) / optional scope (buzz mode). */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
            <Tags size={12} />
            {mode === 'buzz' ? '카테고리 스코프 (선택)' : '카테고리 태그'}
          </div>
          {categoriesLoading ? (
            <p className="text-xs text-muted-foreground">카테고리 불러오는 중…</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <Chip
                  key={c.tag}
                  active={tags.includes(c.tag)}
                  count={c.source_count}
                  onClick={() => toggleTag(c.tag)}
                  disabled={save.isPending}
                >
                  {c.tag}
                </Chip>
              ))}
              {extraTags.map((t) => (
                <Chip
                  key={t}
                  active
                  onClick={() => toggleTag(t)}
                  disabled={save.isPending}
                  aria-label={`커스텀 태그 ${t} 제거`}
                >
                  {t} ✕
                </Chip>
              ))}
              {categories.length === 0 && extraTags.length === 0 && (
                <span className="text-xs text-muted-foreground">
                  {categoriesError ? `카테고리 로드 실패: ${categoriesError}` : '카테고리가 없습니다.'}
                </span>
              )}
            </div>
          )}
          {categoriesError && categories.length > 0 && (
            <p className="text-[11px] text-warning">
              일부 카테고리를 불러오지 못했습니다: {categoriesError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-2">
          {save.isError && (
            <span className="mr-auto text-xs text-destructive">{errorMessage(save.error)}</span>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={save.isPending}
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            저장
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

function ModeToggle({ value, onChange }: { value: SelectMode; onChange: (m: SelectMode) => void }) {
  const opts: { key: SelectMode; label: string; icon: React.ReactNode }[] = [
    { key: 'buzz', label: '화제성', icon: <Flame size={12} /> },
    { key: 'tag', label: '카테고리', icon: <Tags size={12} /> },
  ]
  return (
    <div className="flex shrink-0 rounded-md border border-border p-0.5">
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          aria-pressed={value === o.key}
          className={
            'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition ' +
            (value === o.key
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground')
          }
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ─── ② 토픽 브라우저 ────────────────────────────────────────────────────────

function TopicBrowserTab() {
  const catsQ = useQuery({ queryKey: ['trend-categories'], queryFn: api.getTrendCategories })

  const [tag, setTag] = useState('')
  const [minScore, setMinScore] = useState(0)
  const [spikeOnly, setSpikeOnly] = useState(false)
  const [applied, setApplied] = useState<TrendTopicsQuery>({ limit: 40 })

  const topicsQ = useQuery({
    queryKey: ['trend-topics', applied],
    queryFn: () => api.getTrendTopics(applied),
  })

  const apply = () =>
    setApplied({
      tag: tag || undefined,
      min_score: minScore > 0 ? minScore : undefined,
      spike_only: spikeOnly || undefined,
      limit: 40,
    })

  const createM = useCreateStory()
  const [produce, setProduce] = useState<TrendTopic | null>(null)
  const [pFormat, setPFormat] = useState<StoryFormat>('short')
  const [pLength, setPLength] = useState(60)
  const [pAdapt, setPAdapt] = useState<AdaptationStrength>('balanced')

  const openProduce = (t: TrendTopic) => {
    createM.reset()
    setPFormat('short')
    setPLength(60)
    setPAdapt('balanced')
    setProduce(t)
  }

  const cats = catsQ.data ?? []
  const topics = topicsQ.data ?? []
  const categoryOptions: SelectOption[] = [
    { label: '전체 카테고리', value: '' },
    ...cats.map((c) => ({ label: `${c.tag} (${c.source_count})`, value: c.tag })),
  ]

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <Card>
        <Card.Body className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px] flex-1">
            <div className="mb-1 text-[11px] text-muted-foreground">카테고리</div>
            <Select value={tag} onValueChange={setTag} options={categoryOptions} />
          </div>
          <div>
            <div className="mb-1 text-[11px] text-muted-foreground">최소 점수</div>
            <Input
              type="number"
              min={0}
              step={1}
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply()
              }}
              block={false}
              className="w-24"
            />
          </div>
          <label className="flex h-8 items-center gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              checked={spikeOnly}
              onChange={(e) => setSpikeOnly(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            급상승만
          </label>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={apply}
            loading={topicsQ.isFetching}
          >
            <Sparkles size={14} /> 조회
          </Button>
        </Card.Body>
      </Card>

      <CreateResultBanner
        pending={createM.isPending}
        error={createM.isError ? createM.error : null}
        story={createM.isSuccess ? createM.data : undefined}
      />

      {/* Topics */}
      {topicsQ.isLoading ? (
        <Card>
          <Card.Body>
            <p className="text-sm text-muted-foreground">토픽 불러오는 중…</p>
          </Card.Body>
        </Card>
      ) : topicsQ.isError ? (
        <Card>
          <Card.Body>
            <p className="text-sm text-destructive">토픽 로드 실패: {errorMessage(topicsQ.error)}</p>
          </Card.Body>
        </Card>
      ) : topics.length === 0 ? (
        <Card>
          <EmptyState
            title="표시할 토픽이 없어요."
            description="필터를 완화하거나, 트렌드 수집이 채워질 때까지 기다려 보세요."
          />
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {topics.map((t) => (
            <TopicCard
              key={t.id}
              topic={t}
              onProduce={() => openProduce(t)}
              disabled={createM.isPending}
            />
          ))}
        </div>
      )}

      {/* Produce dialog */}
      <ConfirmDialog
        open={produce != null}
        onOpenChange={(o) => {
          if (!o) setProduce(null)
        }}
        title="이 토픽으로 제작"
        description={produce?.title}
        confirmLabel="제작 시작"
        cancelLabel="취소"
        onConfirm={() => {
          const t = produce
          if (!t) return
          createM.mutate({
            concept: t.title,
            source_material: buildSourceMaterial(t),
            adaptation_strength: pAdapt,
            format: pFormat,
            length: pLength > 0 ? pLength : undefined,
          })
          setProduce(null)
        }}
        body={
          <div className="space-y-3">
            <FormRow label="포맷" stacked>
              <Select
                value={pFormat}
                onValueChange={(v) => setPFormat(v as StoryFormat)}
                options={FORMAT_OPTIONS}
              />
            </FormRow>
            <FormRow label="목표 길이(초)" stacked help="0=자동">
              <Input
                type="number"
                min={0}
                value={pLength}
                onChange={(e) => setPLength(Number(e.target.value))}
              />
            </FormRow>
            <FormRow label="각색 강도" stacked help="원작 충실 ↔ 재창작">
              <Select
                value={pAdapt}
                onValueChange={(v) => setPAdapt(v as AdaptationStrength)}
                options={ADAPT_OPTIONS}
              />
            </FormRow>
          </div>
        }
      />
    </div>
  )
}

function TopicCard({
  topic,
  onProduce,
  disabled,
}: {
  topic: TrendTopic
  onProduce: () => void
  disabled: boolean
}) {
  const dist = Object.entries(topic.source_distribution ?? {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)

  return (
    <Card>
      <Card.Body className="flex min-w-0 flex-col gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <h3 className="min-w-0 flex-1 text-sm font-semibold text-card-foreground">
            {topic.title}
          </h3>
          {topic.spike_flag && (
            <Badge variant="warning" shape="square">
              🔥 급상승
            </Badge>
          )}
          <Badge variant="muted" shape="square">
            {topic.score.toFixed(1)}
          </Badge>
        </div>

        {topic.summary && (
          <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
            {topic.summary}
          </p>
        )}

        {topic.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {topic.keywords.slice(0, 8).map((k) => (
              <Badge key={k} variant="muted" shape="square" className="font-normal">
                {k}
              </Badge>
            ))}
          </div>
        )}

        {dist.length > 0 && (
          <p className="truncate text-[11px] text-muted-foreground">
            출처: {dist.map(([s, n]) => `${s} ${n}`).join(' · ')}
          </p>
        )}

        <div className="flex justify-end pt-1">
          <Button type="button" variant="secondary" size="sm" onClick={onProduce} disabled={disabled}>
            <Sparkles size={14} /> 이 토픽으로 제작
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── ③ 수동 입력 ────────────────────────────────────────────────────────────

function ManualInputTab() {
  const createM = useCreateStory()
  const [material, setMaterial] = useState('')
  const [adapt, setAdapt] = useState<AdaptationStrength>('balanced')
  const [format, setFormat] = useState<StoryFormat>('short')
  const [length, setLength] = useState(60)

  const canSubmit = material.trim().length > 0

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (createM.isPending || !canSubmit) return
    createM.mutate({
      source_material: material.trim(),
      adaptation_strength: adapt,
      format,
      length: length > 0 ? length : undefined,
    })
  }

  return (
    <div className="space-y-3">
      <Card>
        <Card.Header>
          <Card.Title className="text-sm">소재/줄거리 직접 입력</Card.Title>
          <Card.Description>
            줄거리나 소재를 붙여넣으면 컨셉을 자동으로 뽑아 슈팅 플랜을 생성합니다. (컨셉을 따로
            입력하지 않아도 돼요.)
          </Card.Description>
        </Card.Header>
        <Card.Body>
          <form className="space-y-3" onSubmit={onSubmit}>
            <FormRow label="소재 / 줄거리" required stacked help="첫 줄이 컨셉으로 사용됩니다.">
              <Textarea
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                rows={8}
                placeholder="예: 비 오는 밤, 편의점 알바생이 단골 손님에게 우산을 씌워주며 시작되는 짧은 인연…"
                required
              />
            </FormRow>
            <div className="grid gap-3 sm:grid-cols-3">
              <FormRow label="각색 강도" stacked>
                <Select
                  value={adapt}
                  onValueChange={(v) => setAdapt(v as AdaptationStrength)}
                  options={ADAPT_OPTIONS}
                />
              </FormRow>
              <FormRow label="포맷" stacked>
                <Select
                  value={format}
                  onValueChange={(v) => setFormat(v as StoryFormat)}
                  options={FORMAT_OPTIONS}
                />
              </FormRow>
              <FormRow label="목표 길이(초)" stacked help="0=자동">
                <Input
                  type="number"
                  min={0}
                  value={length}
                  onChange={(e) => setLength(Number(e.target.value))}
                />
              </FormRow>
            </div>

            <CreateResultBanner
              pending={createM.isPending}
              error={createM.isError ? createM.error : null}
              story={createM.isSuccess ? createM.data : undefined}
            />

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={createM.isPending}
                disabled={!canSubmit || createM.isPending}
              >
                <Sparkles size={14} /> 스토리 생성
              </Button>
            </div>
          </form>
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── ④ 참조 영상 ────────────────────────────────────────────────────────────

// ReferenceVideoTab adapts a YouTube video (or a whole playlist/channel) into
// stories from its subtitles. The POST is synchronous and can block for a while
// on a playlist — the backend expands it and runs one transcript + one story
// generation per video, returning a per-video result list.
function ReferenceVideoTab() {
  const mutation = useMutation({
    mutationFn: (body: ReferenceAdaptInput) => api.postReferenceVideo(body),
  })
  const [url, setUrl] = useState('')
  const [adapt, setAdapt] = useState<AdaptationStrength>('balanced')
  const [format, setFormat] = useState<StoryFormat>('short')
  const [length, setLength] = useState(60)

  const canSubmit = url.trim().length > 0

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (mutation.isPending || !canSubmit) return
    mutation.mutate({
      url: url.trim(),
      adaptation_strength: adapt,
      format,
      length: length > 0 ? length : 0,
    })
  }

  // 503 = feature not wired server-side (yt-dlp / LLM missing). Surface a
  // dedicated hint instead of the generic error banner.
  const notConfigured =
    mutation.isError && mutation.error instanceof ApiError && mutation.error.status === 503

  const results = mutation.data ?? []
  const successCount = results.filter((r) => r.story_id != null).length

  return (
    <div className="space-y-3">
      <Card>
        <Card.Header>
          <Card.Title className="text-sm">참조 영상 각색</Card.Title>
          <Card.Description>
            유튜브 영상(또는 재생목록/채널) 링크를 주면 자막으로 줄거리를 요약해 각색 스토리를
            만듭니다.
          </Card.Description>
        </Card.Header>
        <Card.Body>
          <form className="space-y-3" onSubmit={onSubmit}>
            <FormRow
              label="유튜브 URL"
              required
              stacked
              help="영상 하나 또는 재생목록/채널 링크 — 재생목록은 영상 수만큼 순차 처리되어 시간이 걸릴 수 있어요."
            >
              <Input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=… 또는 …/playlist?list=…"
                disabled={mutation.isPending}
                required
              />
            </FormRow>
            <div className="grid gap-3 sm:grid-cols-3">
              <FormRow label="각색 강도" stacked>
                <Select
                  value={adapt}
                  onValueChange={(v) => setAdapt(v as AdaptationStrength)}
                  options={ADAPT_OPTIONS}
                  disabled={mutation.isPending}
                />
              </FormRow>
              <FormRow label="포맷" stacked>
                <Select
                  value={format}
                  onValueChange={(v) => setFormat(v as StoryFormat)}
                  options={FORMAT_OPTIONS}
                  disabled={mutation.isPending}
                />
              </FormRow>
              <FormRow label="목표 길이(초)" stacked help="0=자동">
                <Input
                  type="number"
                  min={0}
                  value={length}
                  onChange={(e) => setLength(Number(e.target.value))}
                  disabled={mutation.isPending}
                />
              </FormRow>
            </div>

            {mutation.isPending && (
              <div className="flex items-start gap-2 rounded border border-info-soft bg-info-soft p-3 text-sm text-info-soft-foreground">
                <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin" aria-hidden="true" />
                <span>
                  자막을 받아 각색하는 중… 재생목록·채널 링크면 영상 수만큼 순차 처리되어 수 분 이상
                  걸릴 수 있어요. 창을 닫지 말고 기다려 주세요.
                </span>
              </div>
            )}

            {notConfigured ? (
              <div className="rounded border border-warning-soft bg-warning-soft p-3 text-sm text-warning-soft-foreground">
                참조 영상 기능이 아직 구성되지 않았어요 — 서버에 yt-dlp / LLM 설정이 필요합니다.
                설정에서 LLM을 구성한 뒤 다시 시도하세요.
              </div>
            ) : mutation.isError ? (
              <div className="rounded border border-destructive-soft bg-destructive-soft p-3 text-sm text-destructive-soft-foreground">
                각색 실패: {errorMessage(mutation.error)}
              </div>
            ) : null}

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={mutation.isPending}
                disabled={!canSubmit || mutation.isPending}
              >
                <Sparkles size={14} /> 각색 시작
              </Button>
            </div>
          </form>
        </Card.Body>
      </Card>

      {mutation.isSuccess && (
        <ReferenceResults results={results} successCount={successCount} />
      )}
    </div>
  )
}

// ReferenceResults renders the per-video outcome list. When every video failed
// (successCount === 0 on a non-empty list) it leads with an error summary so
// the operator doesn't have to scan every row to notice nothing landed.
function ReferenceResults({
  results,
  successCount,
}: {
  results: ReferenceResult[]
  successCount: number
}) {
  if (results.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Video size={28} />}
          title="처리된 영상이 없어요."
          description="링크에서 영상을 찾지 못했습니다. URL을 확인하고 다시 시도하세요."
        />
      </Card>
    )
  }

  const allFailed = successCount === 0

  return (
    <Card>
      <Card.Body className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-card-foreground">각색 결과</h3>
          <Badge variant={allFailed ? 'destructive' : 'success'} shape="square">
            성공 {successCount}/{results.length}
          </Badge>
          {successCount > 0 && (
            <Button as={Link} to="/stories" variant="link" size="sm" className="ml-auto">
              스토리에서 모두 보기 →
            </Button>
          )}
        </div>

        {allFailed && (
          <div className="rounded border border-destructive-soft bg-destructive-soft p-3 text-sm text-destructive-soft-foreground">
            모든 영상 각색에 실패했어요. 자막이 없는 영상이거나 처리 중 오류가 났을 수 있습니다. 각
            영상의 사유를 확인하고 다시 시도하세요.
          </div>
        )}

        <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
          {results.map((r, i) => (
            <ReferenceResultRow key={r.video_id || r.url || i} result={r} />
          ))}
        </ul>
      </Card.Body>
    </Card>
  )
}

// ReferenceResultRow is one expanded video: title (linking out to the source),
// a 자막/설명-폴백 origin badge, and either a "스토리 보기" link (story_id) or
// the failure reason in destructive text (error).
function ReferenceResultRow({ result }: { result: ReferenceResult }) {
  const ok = result.story_id != null
  const label = result.title || result.url || result.video_id || '(제목 없음)'

  return (
    <li className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 p-3">
      <div className="min-w-0 flex-1">
        {result.url ? (
          <a
            href={result.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-w-0 max-w-full items-center gap-1 text-sm font-medium text-foreground hover:text-info hover:underline"
          >
            <span className="truncate">{label}</span>
            <ExternalLink size={12} className="shrink-0 text-muted-foreground" aria-hidden="true" />
          </a>
        ) : (
          <span className="block truncate text-sm font-medium text-foreground">{label}</span>
        )}
        {!ok && result.error && (
          <p className="mt-0.5 text-xs leading-relaxed text-destructive">{result.error}</p>
        )}
      </div>

      {result.source && (
        <Badge
          variant={result.source === 'subtitle' ? 'info' : 'warning'}
          shape="square"
          className="font-normal"
        >
          {result.source === 'subtitle' ? '자막' : '설명 폴백'}
        </Badge>
      )}

      {ok ? (
        <Button as={Link} to="/stories" variant="secondary" size="xs">
          스토리 보기 →
        </Button>
      ) : (
        <Badge variant="destructive" shape="square">
          실패
        </Badge>
      )}
    </li>
  )
}

// ─── shared ─────────────────────────────────────────────────────────────────

// CreateResultBanner surfaces the shared synchronous create flow — a story is
// generated inline (planner→director→critic), so show pending/error/success
// with a link across to /stories where the result lives.
function CreateResultBanner({
  pending,
  error,
  story,
}: {
  pending: boolean
  error: unknown
  story?: Story
}) {
  if (pending) {
    return (
      <div className="flex items-center gap-2 rounded border border-info-soft bg-info-soft p-3 text-sm text-info-soft-foreground">
        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
        스토리 제작 중… (planner→director→critic, 수 분 걸릴 수 있어요)
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded border border-destructive-soft bg-destructive-soft p-3 text-sm text-destructive-soft-foreground">
        제작 실패: {errorMessage(error)}
      </div>
    )
  }
  if (story) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded border border-success-soft bg-success-soft p-3 text-sm text-success-soft-foreground">
        <span className="min-w-0 truncate">
          “{story.shooting_plan?.title || story.concept}” 스토리가 생성되었어요.
        </span>
        <Button as={Link} to="/stories" variant="link" size="sm" className="ml-auto">
          스토리에서 보기 →
        </Button>
      </div>
    )
  }
  return null
}

const parseTags = (csv: string | undefined) =>
  (csv ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)

// configToInput rebuilds the full write-side DTO from the current config,
// overriding only the source-selection fields the Sources page owns. This
// preserves every other line setting (profile/schedule/render) on save.
function configToInput(c: FactoryConfig, over: Partial<FactoryConfigInput>): FactoryConfigInput {
  return {
    name: c.name,
    channel_profile_id: c.channel_profile_id,
    daily_target: c.daily_target,
    render_mode: c.render_mode,
    default_visibility: c.default_visibility,
    enabled: c.enabled,
    review_gate: c.review_gate,
    story_format: c.story_format,
    target_length_sec: c.target_length_sec,
    min_scenes: c.min_scenes,
    tags: c.tags,
    select_mode: c.select_mode,
    min_score: c.min_score,
    spike_only: c.spike_only,
    ...over,
  }
}

// buildSourceMaterial flattens a picked topic into the story's SOURCE MATERIAL
// block: the summary plus a keyword line for grounding.
function buildSourceMaterial(t: TrendTopic): string {
  const parts: string[] = []
  const summary = t.summary?.trim()
  if (summary) parts.push(summary)
  if (t.keywords.length > 0) parts.push(`키워드: ${t.keywords.join(', ')}`)
  return parts.join('\n')
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'unknown error'
}
